import { useState } from 'react';
import { useNostr } from '@nostrify/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { NostrEvent } from '@nostrify/nostrify';
import { Check, Copy, CopyPlus, Info, Loader2, Minus, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useAppContext } from '@/hooks/useAppContext';
import { useToast } from '@/hooks/useToast';
import { PUBLICATION_CONTENT_KIND, PUBLICATION_INDEX_KIND } from '@/lib/nkbip01';
import { cn } from '@/lib/utils';

type RelayPresence = 'stored' | 'not-found' | 'unreachable';

interface RelayPresenceResult {
  url: string;
  read: boolean;
  write: boolean;
  status: RelayPresence;
}

/**
 * Probes each of the user's configured relays, live, for a specific event
 * id — answering "where is this event actually stored?" one relay at a
 * time via single-relay queries (`nostr.relay(url)`), rather than the
 * usual pool which deliberately hides which relay an answer came from.
 *
 * Nostr has no global registry of event locations, so this can only check
 * the relays the user already knows about; the dialog says as much.
 */
function useEventRelayPresence(eventId: string) {
  const { nostr } = useNostr();
  const { config } = useAppContext();
  const relays = config.relayMetadata.relays;
  const urlsKey = relays.map((r) => r.url).join(',');

  return useQuery<RelayPresenceResult[]>({
    queryKey: ['event-relay-presence', eventId, urlsKey],
    queryFn: async (c) => {
      return Promise.all(
        relays.map(async ({ url, read, write }): Promise<RelayPresenceResult> => {
          try {
            const signal = AbortSignal.any([c.signal, AbortSignal.timeout(4000)]);
            const events = await nostr.relay(url).query([{ ids: [eventId], limit: 1 }], { signal });
            return { url, read, write, status: events.some((e) => e.id === eventId) ? 'stored' : 'not-found' };
          } catch {
            return { url, read, write, status: 'unreachable' };
          }
        }),
      );
    },
    staleTime: 60_000,
  });
}

/**
 * Re-publishes ("clones") the already-signed event to a relay that
 * doesn't have it. No signer is involved — the event carries its own
 * signature, so anyone holding it can redistribute it, and the relay
 * just validates and stores it like any freshly received event. On
 * success the probe cache is updated so the row flips to "has it".
 */
function useCloneToRelay(event: NostrEvent) {
  const { nostr } = useNostr();
  const { config } = useAppContext();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const urlsKey = config.relayMetadata.relays.map((r) => r.url).join(',');

  return useMutation({
    mutationFn: async (url: string) => {
      await nostr.relay(url).event(event);
      return url;
    },
    onSuccess: (url) => {
      queryClient.setQueryData<RelayPresenceResult[]>(
        ['event-relay-presence', event.id, urlsKey],
        (old) => old?.map((p) => (p.url === url ? { ...p, status: 'stored' } : p)),
      );
      toast({ title: 'Cloned', description: `Published the signed event to ${url.replace(/^wss:\/\//, '')}.` });
    },
    onError: (_error, url) => {
      toast({
        title: 'Clone failed',
        description: `${url.replace(/^wss:\/\//, '')} rejected the event — it may require auth, payment, or not accept this kind.`,
        variant: 'destructive',
      });
    },
  });
}

/** Human label for the kinds that can appear in the details dialog. */
function kindLabel(kind: number): string {
  switch (kind) {
    case PUBLICATION_INDEX_KIND:
      return 'publication';
    case PUBLICATION_CONTENT_KIND:
      return 'publication section';
    default:
      return 'wiki article';
  }
}

function RelayRow({
  relay,
  status,
  onClone,
  cloning,
}: {
  relay: { url: string; read: boolean; write: boolean };
  status: RelayPresence | 'checking';
  /** Republish the signed event to this relay (shown when it doesn't have the event). */
  onClone?: () => void;
  cloning?: boolean;
}) {
  return (
    <li className="flex items-center gap-2 text-sm">
      {status === 'checking' && <Loader2 className="size-4 shrink-0 animate-spin text-muted-foreground" />}
      {status === 'stored' && <Check className="size-4 shrink-0 text-green-600" />}
      {status === 'not-found' && <Minus className="size-4 shrink-0 text-muted-foreground" />}
      {status === 'unreachable' && <X className="size-4 shrink-0 text-destructive" />}
      <span className="min-w-0 truncate font-mono text-xs">{relay.url.replace(/^wss:\/\//, '')}</span>
      <span className="ml-auto flex shrink-0 items-center gap-1.5">
        <span
          className={cn(
            'text-xs',
            status === 'stored' ? 'text-green-600' : status === 'unreachable' ? 'text-destructive' : 'text-muted-foreground',
          )}
        >
          {status === 'checking' && 'checking…'}
          {status === 'stored' && 'has it'}
          {status === 'not-found' && (
            <Button
              type="button"
              variant="outline"
              size="xs"
              disabled={cloning}
              onClick={onClone}
              title="Republish the signed event to this relay"
              className="gap-1"
            >
              {cloning ? <Loader2 className="size-3 animate-spin" /> : <CopyPlus className="size-3" />}
              Clone
            </Button>
          )}
          {status === 'unreachable' && 'unreachable'}
        </span>
        {relay.read && (
          <Badge variant="outline" className="px-1 py-0 text-[10px]">
            read
          </Badge>
        )}
        {relay.write && (
          <Badge variant="outline" className="px-1 py-0 text-[10px]">
            write
          </Badge>
        )}
      </span>
    </li>
  );
}

/** The dialog body — mounted only while the dialog is open, so relays are probed fresh on every open. */
function EventDetailsContent({ event }: { event: NostrEvent }) {
  const { toast } = useToast();
  const { config } = useAppContext();
  const { data: presence, isLoading } = useEventRelayPresence(event.id);
  const clone = useCloneToRelay(event);
  const [copied, setCopied] = useState(false);

  const relays = config.relayMetadata.relays;
  const json = JSON.stringify(event, null, 2);

  const copyJson = async () => {
    try {
      await navigator.clipboard.writeText(json);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast({ title: 'Copied to clipboard' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  return (
    <div className="min-w-0 space-y-5">
      <section>
        <h3 className="mb-2 text-sm font-semibold">Stored on</h3>
        {relays.length === 0 ? (
          <p className="text-sm text-muted-foreground">No relays configured.</p>
        ) : (
          <ul className="space-y-1.5">
            {relays.map((relay) => (
              <RelayRow
                key={relay.url}
                relay={relay}
                status={isLoading ? 'checking' : (presence?.find((p) => p.url === relay.url)?.status ?? 'checking')}
                onClone={() => clone.mutate(relay.url)}
                cloning={clone.isPending && clone.variables === relay.url}
              />
            ))}
          </ul>
        )}
        <p className="mt-2 text-xs text-muted-foreground">
          Checked live against your configured relays — the event may live on relays outside your
          list too. Cloning republishes the already-signed event to a relay that's missing it.
        </p>
      </section>

      <section>
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold">Raw event</h3>
          <Button type="button" variant="outline" size="icon" className="size-7" onClick={copyJson} title="Copy event JSON">
            {copied ? <Check className="size-3.5 text-green-600" /> : <Copy className="size-3.5" />}
          </Button>
        </div>
        <pre className="max-h-72 overflow-auto rounded-lg bg-muted p-3 font-mono text-xs leading-5 break-all whitespace-pre-wrap">
          {json}
        </pre>
      </section>
    </div>
  );
}

interface EventDetailsDialogProps {
  /** The event to inspect (article, publication index, or section). */
  event: NostrEvent;
  /** Display title of the content, for the dialog description. */
  title: string;
  size?: 'sm' | 'default';
  className?: string;
}

/**
 * A "Details" button + dialog for the action bars: shows the raw signed
 * event JSON (with a copy button) and a live probe of which of the user's
 * configured relays actually store the event.
 */
export function EventDetailsDialog({ event, title, size = 'sm', className }: EventDetailsDialogProps) {
  return (
    <Dialog>
      <DialogTrigger asChild>
        <Button type="button" variant="outline" size={size} className={cn('gap-1.5', className)} title="Raw event and relay details">
          <Info className="size-4" />
          Details
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-xl">
        <DialogHeader>
          <DialogTitle>Event details</DialogTitle>
          <DialogDescription className="break-words">
            "{title}" — the signed event (kind {event.kind}) behind this {kindLabel(event.kind)}, and
            where it's stored.
          </DialogDescription>
        </DialogHeader>
        <EventDetailsContent event={event} />
      </DialogContent>
    </Dialog>
  );
}
